import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatTRY } from '@/lib/currencyFormat';
import * as AccountsDB from '@/lib/personalAccountsDb';
import NewMovementModal from './components/NewMovementModal';
import CancelMovementModal from './components/CancelMovementModal';

function getStatusStyle(status: string) {
  const map: Record<string, string> = {
    'active': 'bg-emerald-100 text-emerald-800',
    'closed': 'bg-foreground-100 text-foreground-500',
    'archived': 'bg-foreground-100 text-foreground-400',
  };
  return map[status] || 'bg-foreground-100 text-foreground-500';
}

type MovementFilterKey = 'all' | 'para' | 'urun' | 'altin' | 'doviz';

// Map movement_type to filter category
function getMovementFilterCategory(mt: string): MovementFilterKey {
  if (mt === 'para_verildi' || mt === 'para_alindi') return 'para';
  if (mt === 'urun_verildi' || mt === 'urun_iade') return 'urun';
  if (mt === 'altin_verildi') return 'altin';
  if (mt === 'doviz_verildi') return 'doviz';
  return 'all'; // duzeltme falls into 'all'
}

export default function PersonalAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [account, setAccount] = useState<AccountsDB.PersonalAccount | null>(null);
  const [movements, setMovements] = useState<AccountsDB.PersonalAccountMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewMovement, setShowNewMovement] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<AccountsDB.PersonalAccountMovement | null>(null);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [movementFilter, setMovementFilter] = useState<MovementFilterKey>('all');

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [accRes, movRes] = await Promise.all([
        AccountsDB.getPersonalAccountById(id),
        AccountsDB.getAccountMovements(id),
      ]);
      if (accRes.error) { setError(accRes.error); return; }
      setAccount(accRes.data);
      setMovements(movRes.data || []);
    } catch {
      setError('Veri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Compute summary from movements (not from RPC — independent calculation)
  const summary = useMemo(() => {
    let totalGiven = 0;
    let totalReceived = 0;
    let totalProduct = 0;
    let totalGoldGram = 0;
    let totalCurrency = 0;

    for (const m of movements) {
      if (m.status === 'cancelled') continue;

      if (m.direction === 'receivable') {
        totalGiven += Number(m.principal_amount) || 0;
      } else {
        totalReceived += Number(m.principal_amount) || 0;
      }

      if (m.movement_type === 'urun_verildi') {
        totalProduct += Number(m.principal_amount) || 0;
      }

      if (m.movement_type === 'altin_verildi') {
        totalGoldGram += Number(m.quantity) || 0;
      }

      if (m.movement_type === 'doviz_verildi') {
        totalCurrency += Number(m.principal_amount) || 0;
      }
    }

    return {
      totalGiven,
      totalReceived,
      remaining: totalGiven - totalReceived,
      totalProduct,
      totalGoldGram,
      totalCurrency,
    };
  }, [movements]);

  // Compute running balance for each movement (chronological order)
  const movementsWithBalance = useMemo(() => {
    // Sort oldest first for running balance calculation
    const sorted = [...movements]
      .filter(m => movementFilter === 'all' || getMovementFilterCategory(m.movement_type) === movementFilter || (movementFilter === 'all' && m.movement_type === 'duzeltme'))
      .sort((a, b) => a.movement_date.localeCompare(b.movement_date) || a.created_at.localeCompare(b.created_at));

    let running = 0;
    const result: (AccountsDB.PersonalAccountMovement & { running_balance: number })[] = [];

    for (const m of sorted) {
      if (m.status !== 'cancelled') {
        if (m.direction === 'receivable') {
          running += Number(m.principal_amount) || 0;
        } else {
          running -= Number(m.principal_amount) || 0;
        }
      }
      result.push({ ...m, running_balance: running });
    }

    // Reverse to show newest first
    return result.reverse();
  }, [movements, movementFilter]);

  const lastMovementDate = useMemo(() => {
    const activeMovements = movements.filter(m => m.status !== 'cancelled');
    if (activeMovements.length === 0) return null;
    return activeMovements.sort((a, b) => b.movement_date.localeCompare(a.movement_date))[0].movement_date;
  }, [movements]);

  const handleClose = async () => {
    if (!id) return;
    if (summary.remaining !== 0) {
      if (!confirm(`Bu hesabın ${formatTRY(Math.abs(summary.remaining))} bakiyesi bulunuyor. Yine de kapatmak istiyor musunuz?`)) return;
    } else {
      if (!confirm('Bu hesabı kapatmak istediğinize emin misiniz?')) return;
    }
    const reason = prompt('Kapanma nedeni (opsiyonel):') || undefined;
    setClosing(true);
    const res = await AccountsDB.closePersonalAccount(id, reason);
    setClosing(false);
    if (res.error) { alert(res.error); return; }
    loadData();
  };

  const handleReopen = async () => {
    if (!id || !confirm('Bu hesabı yeniden açmak istediğinize emin misiniz?')) return;
    setReopening(true);
    const res = await AccountsDB.reopenPersonalAccount(id);
    setReopening(false);
    if (res.error) { alert(res.error); return; }
    loadData();
  };

  const handleDelete = async () => {
    if (!id) return;
    const hasMovements = movements.length > 0;
    const msg = hasMovements
      ? 'Bu hesapta hareket geçmişi bulunmaktadır. Hesap kalıcı olarak silinmeyecek, arşivlenecektir. Devam etmek istiyor musunuz?'
      : 'Bu hesabı silmek istediğinize emin misiniz? Hesapta hiç hareket olmadığı için kalıcı olarak silinecektir.';
    if (!confirm(msg)) return;
    const reason = prompt('Silme nedeni (opsiyonel):') || undefined;
    setDeleting(true);
    const res = await AccountsDB.archivePersonalAccount(id, reason);
    setDeleting(false);
    if (res.error) { alert(res.error); return; }
    const result = res.data as any;
    if (result?.deleted_permanently) {
      navigate('/personal-accounts');
    } else {
      loadData();
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleniyor...</h2>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <i className="ri-error-warning-line text-4xl text-red-400"></i>
        <p className="text-foreground-600">{error || 'Hesap bulunamadı.'}</p>
        <button onClick={() => navigate('/personal-accounts')} className="btn-primary text-sm cursor-pointer">
          Hesap Listesine Dön
        </button>
      </div>
    );
  }

  const isActive = account.status === 'active';
  const isArchived = account.status === 'archived';

  const statusLabel = account.status === 'active' ? 'Aktif' : account.status === 'closed' ? 'Kapalı' : 'Arşiv';

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <button
            onClick={() => navigate('/personal-accounts')}
            className="text-sm text-foreground-400 hover:text-foreground-600 cursor-pointer mb-1 flex items-center gap-1"
          >
            <i className="ri-arrow-left-line"></i> Kişisel Hesaplar
          </button>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{account.account_name}</h2>
          <p className="text-xs text-foreground-400 mt-0.5">Şahsi hesap defteri — satış ve müşteri hesaplarından bağımsızdır</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isActive && (
            <>
              <button onClick={() => setShowNewMovement(true)} className="btn-primary whitespace-nowrap flex items-center gap-2 cursor-pointer text-sm">
                <i className="ri-add-line"></i>
                <span>Yeni Hareket</span>
              </button>
              <button onClick={handleClose} disabled={closing} className="px-4 py-2.5 text-sm font-medium text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 cursor-pointer whitespace-nowrap">
                {closing ? 'Kapatılıyor...' : 'Hesabı Kapat'}
              </button>
              <button onClick={handleDelete} disabled={deleting} className="px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap">
                {deleting ? 'Siliniyor...' : 'Hesabı Sil'}
              </button>
            </>
          )}
          {account.status === 'closed' && (
            <>
              <button onClick={handleReopen} disabled={reopening} className="px-4 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 cursor-pointer whitespace-nowrap">
                {reopening ? 'Açılıyor...' : 'Hesabı Yeniden Aç'}
              </button>
              <button onClick={handleDelete} disabled={deleting} className="px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap">
                {deleting ? 'Siliniyor...' : 'Hesabı Sil'}
              </button>
            </>
          )}
          {isArchived && (
            <p className="text-sm text-foreground-400 italic">Bu hesap arşivlenmiş</p>
          )}
        </div>
      </div>

      {/* Kişi Bilgi Kartı */}
      <div className="stat-card p-5">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className="text-sm text-primary-600 font-semibold">{account.account_no}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusStyle(account.status)}`}>
            {statusLabel}
          </span>
          {account.person_type && (
            <span className="text-xs text-foreground-400">
              {AccountsDB.PERSON_TYPE_LABELS[account.person_type] || account.person_type}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
          <div>
            <p className="text-xs text-foreground-400 mb-0.5">Telefon</p>
            <p className="text-foreground-700 font-medium flex items-center gap-1.5">
              {account.phone || '—'}
              {account.phone && (
                <a href={`tel:${account.phone}`} className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-500 hover:bg-emerald-100 cursor-pointer">
                  <i className="ri-phone-line text-[10px]"></i>
                </a>
              )}
            </p>
          </div>
          <div>
            <p className="text-xs text-foreground-400 mb-0.5">İkinci Telefon</p>
            <p className="text-foreground-700 font-medium">{account.secondary_phone || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-400 mb-0.5">Adres</p>
            <p className="text-foreground-700 font-medium truncate">{account.address || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-400 mb-0.5">Açılış Tarihi</p>
            <p className="text-foreground-700 font-medium">{new Date(account.opened_at).toLocaleDateString('tr-TR')}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-400 mb-0.5">Son Hareket</p>
            <p className="text-foreground-700 font-medium">
              {lastMovementDate ? new Date(lastMovementDate).toLocaleDateString('tr-TR') : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-foreground-400 mb-0.5">Açıklama</p>
            <p className="text-foreground-700 font-medium truncate">{account.description || '—'}</p>
          </div>
        </div>
        {account.notes && (
          <div className="mt-3 pt-3 border-t border-background-200">
            <p className="text-xs text-foreground-400 mb-0.5">Not</p>
            <p className="text-sm text-foreground-600">{account.notes}</p>
          </div>
        )}
        {account.status === 'closed' && account.closure_reason && (
          <div className="mt-3 pt-3 border-t border-background-200">
            <p className="text-xs text-foreground-400 mb-0.5">Kapanma Nedeni</p>
            <p className="text-sm text-foreground-600">{account.closure_reason}</p>
          </div>
        )}
        {isArchived && account.deletion_reason && (
          <div className="mt-3 pt-3 border-t border-background-200">
            <p className="text-xs text-foreground-400 mb-0.5">Arşivleme Nedeni</p>
            <p className="text-sm text-foreground-600">{account.deletion_reason}</p>
          </div>
        )}
      </div>

      {/* Özet Kartları — 6 cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Toplam Verilen</p>
          <p className="text-xl font-bold text-amber-600">{formatTRY(summary.totalGiven)}</p>
        </div>
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Toplam Tahsil Edilen</p>
          <p className="text-xl font-bold text-emerald-600">{formatTRY(summary.totalReceived)}</p>
        </div>
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Kalan Bakiye</p>
          <p className={`text-xl font-bold ${summary.remaining > 0 ? 'text-amber-600' : summary.remaining < 0 ? 'text-red-500' : 'text-foreground-500'}`}>
            {formatTRY(Math.abs(summary.remaining))}
          </p>
          <p className="text-[10px] text-foreground-400 mt-0.5">
            {summary.remaining > 0 ? 'kişiden alacak' : summary.remaining < 0 ? 'kişiye borç' : 'bakiyeler eşit'}
          </p>
        </div>
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Toplam Ürün</p>
          <p className="text-xl font-bold text-foreground-700">{formatTRY(summary.totalProduct)}</p>
        </div>
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Toplam Altın</p>
          <p className="text-xl font-bold text-amber-500">{summary.totalGoldGram.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr</p>
        </div>
        <div className="stat-card p-4 text-center">
          <p className="text-xs text-foreground-400 mb-1">Toplam Döviz</p>
          <p className="text-xl font-bold text-foreground-700">{formatTRY(summary.totalCurrency)}</p>
        </div>
      </div>

      {/* Hızlı İşlemler + Filtre Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {isActive && (
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowNewMovement(true)} className="px-4 py-2 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-money-dollar-circle-line"></i> Para Ver
            </button>
            <button onClick={() => setShowNewMovement(true)} className="px-4 py-2 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-bank-card-line"></i> Para Al
            </button>
            <button onClick={() => setShowNewMovement(true)} className="px-4 py-2 text-xs font-medium bg-background-100 text-foreground-600 rounded-lg hover:bg-background-200 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-archive-line"></i> Ürün Ver
            </button>
            <button onClick={() => setShowNewMovement(true)} className="px-4 py-2 text-xs font-medium bg-amber-50 text-amber-600 rounded-lg hover:bg-amber-100 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-copper-coin-line"></i> Altın Ver
            </button>
            <button onClick={() => setShowNewMovement(true)} className="px-4 py-2 text-xs font-medium bg-sky-50 text-sky-700 rounded-lg hover:bg-sky-100 cursor-pointer whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-exchange-dollar-line"></i> Döviz Ver
            </button>
          </div>
        )}
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 self-start">
          {AccountsDB.MOVEMENT_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setMovementFilter(f.key as MovementFilterKey)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                movementFilter === f.key
                  ? 'bg-background-50 text-foreground-900'
                  : 'text-foreground-400 hover:text-foreground-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Movement Ledger — the big notebook-style table */}
      <div className="stat-card overflow-x-auto">
        <div className="p-5 pb-2 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-foreground-800">
            Hareket Defteri
            {movementFilter !== 'all' && (
              <span className="text-xs font-normal text-foreground-400 ml-2">
                ({movementsWithBalance.length} hareket)
              </span>
            )}
          </h4>
          {movements.length === 0 && (
            <span className="text-xs text-foreground-400">Henüz hareket yok</span>
          )}
        </div>
        <div className="px-5 pb-5 pt-2">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-background-200">
                <th className="table-header text-left">Tarih</th>
                <th className="table-header text-left">Tür</th>
                <th className="table-header text-left">Açıklama</th>
                <th className="table-header text-left">Ürün / Varlık</th>
                <th className="table-header text-right">Miktar</th>
                <th className="table-header text-left">Birim</th>
                <th className="table-header text-right">Tutar</th>
                <th className="table-header text-right">Kalan Bakiye</th>
                <th className="table-header text-left">Not</th>
                <th className="table-header text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {movementsWithBalance.map(m => {
                const isCancelled = m.status === 'cancelled';
                const mtLabel = AccountsDB.MOVEMENT_TYPE_LABELS[m.movement_type] || m.movement_type;

                // Determine asset/product display
                let assetDisplay = '—';
                if (m.product_name) {
                  assetDisplay = m.product_name + (m.model_code ? ` (${m.model_code})` : '');
                } else if (m.asset_name) {
                  assetDisplay = m.asset_name;
                }

                // Determine quantity display
                let qtyDisplay = '—';
                if (m.movement_type === 'altin_verildi') {
                  const gt = AccountsDB.GOLD_TYPE_OPTIONS.find(g => AccountsDB.GOLD_TYPE_LABELS[g].label === m.asset_name);
                  if (gt && m.asset_name) {
                    qtyDisplay = `${Number(m.quantity).toLocaleString('tr-TR')} ${m.asset_name}`;
                  } else {
                    qtyDisplay = `${Number(m.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr`;
                  }
                } else if (m.movement_type === 'doviz_verildi' && m.currency_code && m.currency_code !== 'TRY') {
                  qtyDisplay = `${Number(m.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${m.currency_code}`;
                } else if (m.quantity && m.quantity !== 1) {
                  qtyDisplay = Number(m.quantity).toLocaleString('tr-TR');
                } else if (m.unit_price) {
                  qtyDisplay = `1 × ${formatTRY(m.unit_price)}`;
                }

                const unitDisplay = m.movement_type === 'altin_verildi' ? 'gr' : (m.unit || '—');

                const isGiving = m.direction === 'receivable';

                return (
                  <tr key={m.id} className={`border-b border-background-100 ${isCancelled ? 'opacity-30 line-through' : 'hover:bg-background-50'}`}>
                    <td className="table-cell text-xs text-foreground-500 whitespace-nowrap">
                      {new Date(m.movement_date).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="table-cell text-xs whitespace-nowrap">
                      <span className={`${isGiving ? 'text-amber-600' : 'text-emerald-600'} font-medium`}>
                        {mtLabel}
                      </span>
                    </td>
                    <td className="table-cell text-xs text-foreground-500 max-w-[150px] truncate">
                      {m.description || m.reason || '—'}
                    </td>
                    <td className="table-cell text-xs text-foreground-600 max-w-[160px] truncate">
                      {assetDisplay}
                    </td>
                    <td className="table-cell text-xs text-foreground-700 text-right whitespace-nowrap font-medium">
                      {qtyDisplay}
                    </td>
                    <td className="table-cell text-xs text-foreground-400 whitespace-nowrap">
                      {unitDisplay}
                    </td>
                    <td className="table-cell text-sm text-right whitespace-nowrap">
                      <span className={isGiving ? 'text-amber-600 font-semibold' : 'text-emerald-600 font-semibold'}>
                        {isGiving ? '+' : '-'}{formatTRY(m.principal_amount)}
                      </span>
                      {/* Show original currency/gold info */}
                      {m.currency_code && m.currency_code !== 'TRY' && m.quantity > 0 && (
                        <p className="text-[10px] text-foreground-400">
                          {Number(m.quantity).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {m.currency_code}
                          {m.unit_price ? ` × ${Number(m.unit_price).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}` : ''}
                        </p>
                      )}
                    </td>
                    <td className="table-cell text-sm text-right whitespace-nowrap">
                      <span className={`font-semibold ${m.running_balance > 0 ? 'text-amber-600' : m.running_balance < 0 ? 'text-red-500' : 'text-foreground-500'}`}>
                        {formatTRY(Math.abs(m.running_balance))}
                      </span>
                    </td>
                    <td className="table-cell text-xs text-foreground-400 max-w-[120px] truncate">
                      {m.cancellation_reason ? `İptal: ${m.cancellation_reason}` : m.notes || '—'}
                    </td>
                    <td className="table-cell text-center py-2">
                      {!isCancelled && isActive && (
                        <button
                          onClick={() => setCancelTarget(m)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 cursor-pointer"
                          title="Hareketi iptal et"
                        >
                          <i className="ri-close-circle-line text-sm"></i>
                        </button>
                      )}
                      {isCancelled && (
                        <span className="text-[10px] text-foreground-300">İptal</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {movementsWithBalance.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-sm text-foreground-400">
                    {movements.length === 0
                      ? 'Henüz hareket kaydı yok. Yukarıdaki butonlardan birine tıklayarak ilk hareketi ekleyebilirsiniz.'
                      : 'Bu filtreye uygun hareket bulunamadı.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showNewMovement && account && (
        <NewMovementModal
          accountId={account.id}
          onClose={() => setShowNewMovement(false)}
          onSuccess={() => { setShowNewMovement(false); loadData(); }}
        />
      )}

      {cancelTarget && (
        <CancelMovementModal
          movement={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onSuccess={() => { setCancelTarget(null); loadData(); }}
        />
      )}
    </div>
  );
}